import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { useCompleteVulModalStore } from './Modals/CompleteVulModal/CompleteVulModalStore';
import { VulnerabilityPopupProps } from './VulnerabilityPopupTypes';

type ExtendedVulnerabilityPopupProps = VulnerabilityPopupProps & {
  isActiveFilterArchive?: boolean;
};

export const VulnerabilityPopup: React.FC<ExtendedVulnerabilityPopupProps> = ({
  vulnerability,
  onMove,
  onSave,
  isMoving,
  isLoading,
  isActiveFilterArchive,
}) => {
  const { t } = useTranslation();
  const { onCompleteVulOpen } = useCompleteVulModalStore();
  const { enqueueSnackbar } = useSnackbar();

  const translatedCategory = () => {
    switch (vulnerability.category) {
      case 'sanitary':
        return t('MapModals.AddVul.categories.sanitary');
      case 'technical':
        return t('MapModals.AddVul.categories.technical');
      case 'staff':
        return t('MapModals.AddVul.categories.staff');
      case 'environmental':
        return t('MapModals.AddVul.categories.environmental');
      default:
        return vulnerability.category;
    }
  };

  const translatedCriticality = () => {
    switch (vulnerability.criticality) {
      case 'critical':
        return t('MapModals.AddVul.criticalities.critical');
      case 'high':
        return t('MapModals.AddVul.criticalities.high');
      case 'medium':
        return t('MapModals.AddVul.criticalities.medium');
      case 'low':
        return t('MapModals.AddVul.criticalities.low');
      default:
        return vulnerability.criticality;
    }
  };

  const handleResolveClick = () => {
    if (isActiveFilterArchive) {
      enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
      return;
    }
    onCompleteVulOpen(vulnerability.id);
  };

  return (
    <div className="trap-popup">
      <div className="trap-popup-header">
        <strong>{t('Popups.VulnerabilityPopup.title')}</strong>
      </div>
      <div>{t('Popups.VulnerabilityPopup.category', { category: translatedCategory() })}</div>
      <div>{t('Popups.VulnerabilityPopup.criticality', { criticality: translatedCriticality() })}</div>
      <div>{t('Popups.VulnerabilityPopup.description', { comment: vulnerability.comment ?? '' })}</div>
      <div className="row align-content-center text-center mt-2">
        <div className="col-12 align-items-center text-center">
          {isMoving ? (
            <button
              type="button"
              className="btn btn-ghost-success custom-toggle d-flex align-items-center justify-content-center mx-auto gap-2"
              data-bs-toggle="button"
              onClick={onSave}
              disabled={isLoading}
            >
              <span className="icon-on">
                <i className="ri-save-3-fill align-bottom" />
              </span>
              <span className="icon-off">
                <i className="ri-save-3-fill align-bottom" />
              </span>
              {isLoading ? t('Popups.VulnerabilityPopup.saving') : t('Popups.VulnerabilityPopup.savePosition')}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-ghost-primary custom-toggle d-flex align-items-center justify-content-center mx-auto gap-2"
              onClick={onMove}
              disabled={isLoading}
            >
              <span className="icon-on">
                <i className="ri-drag-move-2-fill align-bottom" />
              </span>
              <span className="icon-off">
                <i className="ri-drag-move-2-fill align-bottom" />
              </span>
              {t('Popups.VulnerabilityPopup.changePosition')}
            </button>
          )}
        </div>
      </div>
      <div className="row align-items-center text-center">
        <div className="col-xl-12">
          <button
            type="button"
            className="mt-2"
            style={{
              border: '1px solid #6A37D4',
              backgroundColor: 'transparent',
              color: '#333',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
            onClick={handleResolveClick}
          >
            {t('Popups.VulnerabilityPopup.resolveVulnerability')}
          </button>
        </div>
      </div>
    </div>
  );
};
